// MapLibre GL boot for the trail map. The Streets basemap uses the same
// OpenFreeMap vector style as the native app so its text layers can stay above
// raster imagery as an independently toggleable "Street names" layer.
import { MAPLIBRE_STYLE_URL, mapLibreRasterSource } from "../tile_sources"
import { FOLLOW_PITCH_MAX } from "./camera_gesture"
import { logEvent } from "./client_logger"

const OPENFREEMAP_ATTRIBUTION = 'OpenFreeMap © OpenMapTiles Data from <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
const BASEMAP_LAYER_IDS = Object.freeze([
  "basemap-satellite",
  "basemap-hybrid-ref",
  "basemap-topo"
])
const baseMapStates = new WeakMap()
const NUMERIC_COMPARISONS = new Set(["<", "<=", ">", ">=", "==", "!="])

// A local-style fallback keeps the map usable if the vector style endpoint is
// unavailable. Its OSM raster already contains labels, so the Street names
// toggle becomes best-effort only during that upstream outage.
export function fallbackBaseStyle() {
  return {
    version: 8,
    sources: {
      streets: mapLibreRasterSource("streets"),
      satellite: mapLibreRasterSource("satellite"),
      topo: mapLibreRasterSource("topo")
    },
    layers: [
      { id: "basemap-streets", type: "raster", source: "streets" },
      { id: "basemap-satellite", type: "raster", source: "satellite", layout: { visibility: "none" } },
      { id: "basemap-hybrid-ref", type: "raster", source: "topo", layout: { visibility: "none" }, paint: { "raster-opacity": 0.35 } },
      { id: "basemap-topo", type: "raster", source: "topo", layout: { visibility: "none" } }
    ]
  }
}

// Keep only text-bearing symbol layers from the shared vector style. This
// avoids POI icons, one-way arrows, and 3D buildings floating over imagery,
// while preserving street, place, and water names.
export function mapStyleWithBasemaps(style) {
  if (!style || style.version !== 8 || !style.sources || !Array.isArray(style.layers)) {
    throw new Error("Invalid MapLibre base style")
  }

  const sources = Object.fromEntries(Object.entries(style.sources).map(([id, source]) => [
    id,
    id === "openmaptiles" && !source.attribution
      ? { ...source, attribution: OPENFREEMAP_ATTRIBUTION }
      : { ...source }
  ]))
  sources["gridx-basemap-satellite"] = mapLibreRasterSource("satellite")
  sources["gridx-basemap-topo"] = mapLibreRasterSource("topo")

  const layers = style.layers
    .filter(layer => layer?.type !== "fill-extrusion")
    .filter(layer => layer?.type !== "symbol" || layer.layout?.["text-field"] != null)
    .map(layer => {
      const safeLayer = layer.filter ? { ...layer, filter: nullSafeStyleFilter(layer.filter) } : { ...layer }
      if (safeLayer.type !== "symbol" || safeLayer.layout?.["icon-image"] == null) return safeLayer
      const layout = { ...safeLayer.layout }
      delete layout["icon-image"]
      return { ...safeLayer, layout }
    })

  const firstLabel = layers.findIndex(layer => layer.type === "symbol")
  const insertAt = firstLabel < 0 ? layers.length : firstLabel
  layers.splice(insertAt, 0,
    { id: "basemap-satellite", type: "raster", source: "gridx-basemap-satellite", layout: { visibility: "none" } },
    { id: "basemap-hybrid-ref", type: "raster", source: "gridx-basemap-topo", layout: { visibility: "none" }, paint: { "raster-opacity": 0.35 } },
    { id: "basemap-topo", type: "raster", source: "gridx-basemap-topo", layout: { visibility: "none" } }
  )

  return { ...style, sources, layers }
}

// OpenMapTiles properties such as rank, capital, and ref_length are nullable.
// Numeric filter expressions otherwise emit one worker warning per affected
// feature. Type them with an out-of-range fallback that makes a missing value
// fail ordered/equality tests (and pass !=), without changing valid features.
export function nullSafeStyleFilter(expression) {
  if (!Array.isArray(expression)) return expression
  const safe = expression.map(part => nullSafeStyleFilter(part))
  const operator = safe[0]
  if (!NUMERIC_COMPARISONS.has(operator)) return safe

  const leftIsGet = getExpression(safe[1])
  const rightIsGet = getExpression(safe[2])
  if (leftIsGet && typeof safe[2] === "number") {
    safe[1] = ["number", safe[1], missingNumberFallback(operator, true)]
  } else if (rightIsGet && typeof safe[1] === "number") {
    safe[2] = ["number", safe[2], missingNumberFallback(operator, false)]
  }
  return safe
}

function getExpression(value) {
  return Array.isArray(value) && value[0] === "get" && typeof value[1] === "string"
}

function missingNumberFallback(operator, getOnLeft) {
  if (operator === "<" || operator === "<=") {
    return getOnLeft ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER
  }
  if (operator === ">" || operator === ">=") {
    return getOnLeft ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER
  }
  return Number.MAX_SAFE_INTEGER
}

export async function loadBaseStyle({ fetchImpl = fetch, timeoutMs = 8000 } = {}) {
  const abort = typeof AbortController === "undefined" ? null : new AbortController()
  const timer = abort && timeoutMs > 0 ? setTimeout(() => abort.abort(), timeoutMs) : null
  try {
    const response = await fetchImpl(MAPLIBRE_STYLE_URL, {
      credentials: "omit",
      ...(abort ? { signal: abort.signal } : {})
    })
    if (!response.ok) throw new Error(`Map style request failed (${response.status})`)
    return mapStyleWithBasemaps(await response.json())
  } catch (error) {
    logEvent("map.vector_style_unavailable", error)
    return fallbackBaseStyle()
  } finally {
    if (timer) clearTimeout(timer)
  }
}

export function createBaseMap(maplibregl, container, { lat, lng, zoom, style = fallbackBaseStyle() }) {
  const map = new maplibregl.Map({
    container,
    style,
    center: [lng, lat],
    zoom,
    maxPitch: FOLLOW_PITCH_MAX,
    attributionControl: { compact: true }
  })
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right")
  return map
}

// mode: streets | satellite | hybrid | topo
export function applyBasemap(map, mode, { streetNames = true } = {}) {
  const state = baseMapState(map)
  const show = (id, on) => map.setLayoutProperty(id, "visibility", on ? "visible" : "none")
  if (map.getLayer("basemap-streets")) show("basemap-streets", mode === "streets")
  if (map.getLayer("basemap-satellite")) show("basemap-satellite", mode === "satellite" || mode === "hybrid")
  if (map.getLayer("basemap-hybrid-ref")) show("basemap-hybrid-ref", mode === "hybrid")
  if (map.getLayer("basemap-topo")) show("basemap-topo", mode === "topo")

  // The fetched vector style is text-only at the symbol layer. Hide/show those
  // layers directly for Streets, and move raster basemaps below them when the
  // option is on so the same labels remain readable over imagery.
  state.labels.forEach(label => {
    if (map.getLayer(label.id)) {
      map.setLayoutProperty(label.id, "visibility", streetNames ? label.visibility : "none")
    }
  })
  if (!state.labelAnchorId || mode === "streets") return

  const beforeId = streetNames ? state.labelAnchorId : state.overlayAnchorId
  if (!beforeId || !map.getLayer(beforeId)) return
  BASEMAP_LAYER_IDS.forEach(id => {
    if (map.getLayer(id)) map.moveLayer(id, beforeId)
  })
}

function baseMapState(map) {
  if (baseMapStates.has(map)) return baseMapStates.get(map)
  const layers = map.getStyle()?.layers || []
  const labels = layers
    .filter(layer => layer.type === "symbol" && !layer.id.startsWith("lyr-"))
    .map(layer => ({ id: layer.id, visibility: layer.layout?.visibility || "visible" }))
  const state = {
    labels,
    labelAnchorId: labels[0]?.id || null,
    overlayAnchorId: layers.find(layer => layer.id.startsWith("lyr-"))?.id || null
  }
  baseMapStates.set(map, state)
  return state
}
